import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import styles from '@/styles/Categories.module.css';
import { getProductInfo, selectItem } from '@/utils/analytics.js';
import Link from 'next/link';
import React, { useEffect, useState, useContext } from 'react';

import { SnackBar, OffersSnackbar, ProductCatalogueV2, ProductAisleV2 } from '@desquared/wave-components-library';
import {
  addProduct,
  addQuantityLimit,
  removeProduct,
  openListSideSheet,
  getBundleOfferDescription,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
} from '@/utils/products';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { checkListStatus } from '@/utils/lists';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { CurrencyContext } from '@/contexts/CurrencyProvider';

export default function CategoriesAisles({
  aisles,
  hasPadding,
  limitations,
  bundleOffersSummary,
  isOffers = false,
  lastAisleRef,
  isLoading,
  defaultProductImage,
}) {
  const isMobile = useWindowUnderThreshold(850);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const t = useTranslations();
  const { locale, defaultLocale } = useRouter();
  const { formatter } = useContext(CurrencyContext);

  useEffect(() => {
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);

    addQuantityLimit(aisles, setShowSnackbar, limitations);
    return () => {
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  return (
    <>
      {aisles.length ? (
        <>
          {aisles.map((i, index) => {
            return (
              <div
                className={styles.aislesRow}
                key={i.category._id}
                ref={index === aisles.length - 1 ? lastAisleRef : undefined}
              >
                <ProductAisleV2
                  currencyFormatter={formatter}
                  title={i.category?.name || i.category?.text}
                  products={i.products}
                  linkText={t('categories.aisles.all_products_link')}
                  productsLink={
                    isOffers
                      ? `/offers/${i.category.slugAscii}?withOffer=true&withBundleOffers=true`
                      : `/categories/${i.category.slugAscii}`
                  }
                  tilesSize={'default'}
                  isMobile={isMobile}
                  hasPadding={hasPadding}
                  onListClick={openListSideSheet}
                  checkStatus={checkListStatus}
                  addProduct={product =>
                    addProduct(
                      product,
                      false,
                      {
                        item_list_id: isOffers ? 'offers' : 'categories',
                        item_list_name: isOffers ? 'offers' : 'categories',
                      },
                      false,
                      t('common.asides.side_cart.no_comment')
                    )
                  }
                  removeProduct={product =>
                    removeProduct(
                      product,
                      {
                        item_list_id: isOffers ? 'offers' : 'categories',
                        item_list_name: isOffers ? 'offers' : 'categories',
                      },
                      t('common.asides.side_cart.no_comment')
                    )
                  }
                  LinkComponent={props => {
                    const propsWithCleanUrl = getProductPropsWithCleanUrl(props);

                    return (
                      <Link
                        prefetch={false}
                        {...propsWithCleanUrl}
                        onClick={() => {
                          const product = getProductBasedOnSlug(i.products, propsWithCleanUrl.href);

                          if (product) {
                            selectItem(getProductInfo(product?.originalValues), {
                              item_list_id: isOffers ? 'offers' : 'categories',
                              item_list_name: isOffers ? 'offers' : 'categories',
                              index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                            });
                          }
                        }}
                      />
                    );
                  }}
                  allText={t('categories.aisles.all_products_btn')}
                  categoryID={i.category._id}
                  defaultQsText={t('common.qs.default')}
                  defaultQsUnavailableText={t('common.qs.default_unavailable')}
                  ImageComponent={CustomImage}
                  offerLabelHasRibbon={process.env.offerLabelHasRibbon}
                  isTileDetailsAnimated={process.env.isTileDetailsAnimated}
                  ctaIcon={process.env.ctaIcon}
                  listIcon={process.env.listIcon}
                  setMaxSnackBar={setShowSnackbar}
                  hasRoundedCorners={process.env.hasRoundedCorners}
                  defaultProductImage={defaultProductImage}
                  forYouLabel={t('common.user.profile.menu.for_you')}
                />
                <SnackBar
                  open={showSnackBar}
                  setOpen={setShowSnackbar}
                  text={t('common.snackbar')}
                  position={'center'}
                />
              </div>
            );
          })}
          {isLoading ? (
            <div className={styles.aislesLoader}>
              <ProductCatalogueV2 isLoading loaderTilesAmount={5} products={new Array(1)} />
            </div>
          ) : null}
        </>
      ) : isLoading ? (
        <div className={styles.aislesLoader}>
          <ProductCatalogueV2 isLoading loaderTilesAmount={15} products={new Array(1)} />
        </div>
      ) : null}
      <OffersSnackbar
        emoji="🎉"
        open={showBundleOffersSnackBar}
        setOpen={BundleOfferSnackBarOpen.update}
        hasCloseOption={true}
        position="center"
        type="warning"
        autoWidth={true}
        buttonText={t('common.offers_snack_bar.btn_label')}
        text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
        onButtonPress={() => {
          setShowBundleOffersSnackBar(false);
          BundleOfferSnackBarOpen.update(false);
          BundleOfferSideSheetStore.update(true);
        }}
        autoHideDuration={8000}
        closeOnClickAway={true}
        hasShadow={true}
      />
    </>
  );
}
